import { useCallback, useEffect, useRef } from "react";

const LAST_SHOWN_KEY = "lastHelpDialogShown";
const ONE_HOUR_MS = 60 * 60 * 1000;

// How far (in px) the user has to scroll before the help dialog auto-opens.
const SCROLL_THRESHOLD_PX = 300;

export const shouldShowHelpDialog = () => {
    const lastShownTime = localStorage.getItem(LAST_SHOWN_KEY);
    if (!lastShownTime) return true;

    const timeSinceLastShown = Date.now() - parseInt(lastShownTime);
    return timeSinceLastShown >= ONE_HOUR_MS;
};

export const markHelpDialogShown = () => {
    localStorage.setItem(LAST_SHOWN_KEY, Date.now().toString());
};

/**
 * Auto-opens the help dialog the first time the user scrolls past
 * SCROLL_THRESHOLD_PX, provided it hasn't been shown in the last hour.
 *
 * Listens to window scroll by default. Returns a function that can also be
 * called with a scroll offset from a custom scroll container (e.g. a
 * react-window grid) so pages that don't scroll the window still trigger it.
 */
export const useOpenHelpDialogOnScroll = (setHelpDialogOpen) => {
    const triggered = useRef(false);

    const openIfScrolled = useCallback(
        (scrollOffset) => {
            if (triggered.current || scrollOffset < SCROLL_THRESHOLD_PX) return;

            // only evaluate once per page load
            triggered.current = true;

            if (shouldShowHelpDialog()) {
                setHelpDialogOpen(true);
                markHelpDialogShown();
            }
        },
        [setHelpDialogOpen]
    );

    useEffect(() => {
        const handleScroll = () => openIfScrolled(window.scrollY);

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [openIfScrolled]);

    return openIfScrolled;
};

export default useOpenHelpDialogOnScroll;
